import { useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Upload } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  FormField,
} from '../design';
import { ApiCallError } from '../lib/api';
import { useUploadPricingSheet } from '../lib/pricing-sheets';

const ACCEPTED_EXTENSIONS = /\.(xlsx|xls|csv)$/i;
const MAX_BYTES = 10 * 1024 * 1024;

interface PricingSheetUploadModalProps {
  supplierId: string;
  onUploaded?: (sheetId: string) => void;
}

/**
 * Upload entry-point on the supplier detail page (Pass 1).
 *
 * The modal accepts .xlsx/.xls/.csv ≤ 10 MB and posts multipart to
 * /api/pricing-sheets. On success it closes itself and calls onUploaded with
 * the new sheet's id — the parent decides whether to navigate or stay put.
 */
export function PricingSheetUploadModal({ supplierId, onUploaded }: PricingSheetUploadModalProps) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const upload = useUploadPricingSheet();
  const navigate = useNavigate();

  function reset() {
    setFile(null);
    setClientError(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  function validate(f: File): string | null {
    if (!ACCEPTED_EXTENSIONS.test(f.name)) {
      return 'File must be .xlsx, .xls, or .csv.';
    }
    if (f.size === 0) return 'File is empty.';
    if (f.size > MAX_BYTES) {
      return `File exceeds the ${Math.round(MAX_BYTES / 1024 / 1024)} MB upload limit.`;
    }
    return null;
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    if (!picked) {
      setFile(null);
      setClientError(null);
      return;
    }
    const err = validate(picked);
    if (err) {
      setFile(null);
      setClientError(err);
      return;
    }
    setFile(picked);
    setClientError(null);
  }

  async function handleUpload() {
    if (!file) return;
    setClientError(null);
    try {
      const result = await upload.mutateAsync({ supplierId, file });
      setOpen(false);
      reset();
      if (onUploaded) {
        onUploaded(result.id);
      } else {
        navigate({ to: '/pricing-sheets/$id', params: { id: result.id } });
      }
    } catch (err) {
      if (err instanceof ApiCallError) {
        setClientError(err.message);
      } else {
        setClientError((err as Error).message ?? 'Upload failed.');
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm" leadingIcon={<Upload className="size-4" />}>
          Upload pricing sheet
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload pricing sheet</DialogTitle>
          <DialogDescription>
            .xlsx, .xls, or .csv up to 10 MB. The sheet is queued for AI column-mapping + ROI
            scoring once it lands.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FormField label="File" htmlFor="pricing-sheet-file" required>
            <input
              ref={inputRef}
              id="pricing-sheet-file"
              type="file"
              accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-fg-secondary file:mr-3 file:rounded-sm file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-fg-primary hover:file:bg-surface-3"
            />
          </FormField>

          {file && (
            <p className="text-xs text-fg-tertiary">
              <span className="font-mono">{file.name}</span> · {Math.ceil(file.size / 1024)} KB
            </p>
          )}

          {clientError && <p className="text-sm text-negative">{clientError}</p>}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={handleUpload}
            loading={upload.isPending}
            disabled={!file || upload.isPending}
            size="sm"
          >
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
